import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RedeemService from '../services/RedeemService';

const CheckCodePage = () => {
    const navigate = useNavigate();

    // State management
    const [formData, setFormData] = useState({
        productSerial: '',
        scratchCode: '',
        sellerPhone: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [scratchCodeValidation, setScratchCodeValidation] = useState({
        isValid: null,
        isChecking: false,
        message: ''
    });
    const [serialValidation, setSerialValidation] = useState({
        isValid: null,
        isChecking: false,
        message: ''
    });
    const [phoneValidation, setPhoneValidation] = useState({
        isValid: null,
        message: ''
    });

    // Debounced serial validation
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (formData.productSerial.trim().length > 0) {
                validateSerialNumber(formData.productSerial.trim());
            } else {
                setSerialValidation({
                    isValid: null,
                    isChecking: false,
                    message: ''
                });
            }
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [formData.productSerial]);

    // Debounced scratch code validation
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (formData.scratchCode.trim().length > 0) {
                validateScratchCode(formData.scratchCode.trim());
                // Also re-validate serial if it exists
                if (formData.productSerial.trim().length > 0) {
                    validateSerialWithScratchCode(formData.scratchCode.trim(), formData.productSerial.trim());
                }
            } else {
                setScratchCodeValidation({
                    isValid: null,
                    isChecking: false,
                    message: ''
                });
                // Reset serial validation when scratch code is empty
                if (formData.productSerial.trim().length > 0) {
                    validateSerialNumber(formData.productSerial.trim());
                }
            }
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [formData.scratchCode, formData.productSerial]);

    // Phone validation
    useEffect(() => {
        if (formData.sellerPhone.trim().length > 0) {
            validatePhoneNumber(formData.sellerPhone.trim());
        } else {
            setPhoneValidation({
                isValid: null,
                message: ''
            });
        }
    }, [formData.sellerPhone]);

    const validateSerialNumber = async (serialNumber) => {
        setSerialValidation({
            isValid: null,
            isChecking: true,
            message: ''
        });

        try {
            const response = await RedeemService.checkScratchCode('', serialNumber);
            const result = response.data;
            const isValid = result.success || result.valid || result.isValid || false;

            setSerialValidation({
                isValid: isValid,
                isChecking: false,
                message: isValid ? 'Serial sản phẩm hợp lệ' : 'Serial sản phẩm không hợp lệ'
            });
        } catch (error) {
            console.error('Error validating serial number:', error);
            setSerialValidation({
                isValid: false,
                isChecking: false,
                message: 'Serial sản phẩm không hợp lệ'
            });
        }
    };

    const validateScratchCode = async (scratchCode) => {
        setScratchCodeValidation({
            isValid: null,
            isChecking: true,
            message: ''
        });

        try {
            const response = await RedeemService.checkScratchCode(scratchCode);
            const result = response.data;
            const isValid = result.success || result.valid || result.isValid || false;

            setScratchCodeValidation({
                isValid: isValid,
                isChecking: false,
                message: isValid ? '' : 'Mã phủ cào không hợp lệ'
            });
        } catch (error) {
            console.error('Error validating scratch code:', error);
            setScratchCodeValidation({
                isValid: false,
                isChecking: false,
                message: 'Mã phủ cào không hợp lệ'
            });
        }
    };

    const validateSerialWithScratchCode = async (scratchCode, serialNumber) => {
        setSerialValidation({
            isValid: null,
            isChecking: true,
            message: ''
        });

        try {
            const response = await RedeemService.checkScratchCode(scratchCode, serialNumber);
            const result = response.data;
            const isValid = result.success || result.valid || result.isValid || false;

            setSerialValidation({
                isValid: isValid,
                isChecking: false,
                message: isValid ? 'Serial sản phẩm hợp lệ' : 'Serial sản phẩm không hợp lệ'
            });
        } catch (error) {
            console.error('Error validating serial with scratch code:', error);
            setSerialValidation({
                isValid: false,
                isChecking: false,
                message: 'Serial sản phẩm không hợp lệ'
            });
        }
    };

    const validatePhoneNumber = (phone) => {
        // List of hotline numbers that should not be used
        const hotlineNumbers = ['0896357627', '0902855681', '0909458534'];

        // Check if phone is a hotline number
        if (hotlineNumbers.includes(phone)) {
            setPhoneValidation({
                isValid: false,
                message: 'Vui lòng nhập SĐT của người bán hàng cho bạn, không nhập số Hotline trên bao bì'
            });
            return;
        }

        // Vietnamese phone number: 10 digits, starts with 0
        const phoneRegex = /^0\d{9}$/;
        const isValid = phoneRegex.test(phone);

        setPhoneValidation({
            isValid: isValid,
            message: isValid ? '' : 'Số điện thoại không hợp lệ (phải có 10 số và bắt đầu bằng 0)'
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const showMessage = (text, type) => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    const validateForm = () => {
        if (!formData.productSerial.trim()) {
            showMessage('Vui lòng nhập số serial tem', 'error');
            return false;
        }
        if (serialValidation.isValid === false) {
            showMessage('Serial sản phẩm không hợp lệ', 'error');
            return false;
        }
        if (serialValidation.isChecking) {
            showMessage('Đang kiểm tra serial sản phẩm, vui lòng đợi...', 'error');
            return false;
        }
        if (!formData.scratchCode.trim()) {
            showMessage('Vui lòng nhập mã dưới lớp phủ cào', 'error');
            return false;
        }
        if (scratchCodeValidation.isValid === false) {
            showMessage('Mã phủ cào không hợp lệ', 'error');
            return false;
        }
        if (scratchCodeValidation.isChecking) {
            showMessage('Đang kiểm tra mã phủ cào, vui lòng đợi...', 'error');
            return false;
        }
        if (!formData.sellerPhone.trim()) {
            showMessage('Vui lòng nhập số điện thoại người bán', 'error');
            return false;
        }
        if (phoneValidation.isValid === false) {
            showMessage('Số điện thoại người bán không hợp lệ', 'error');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        setMessage({ text: '', type: '' });

        try {
            // Navigate to result page with query params
            const resultParams = new URLSearchParams({
                productSerial: formData.productSerial,
                scratchCode: formData.scratchCode,
                sellerPhone: formData.sellerPhone
            });
            navigate(`/check-result?${resultParams.toString()}`);
        } catch (error) {
            console.error('Error during check:', error);
            showMessage('Có lỗi xảy ra, vui lòng thử lại', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="gradient-bg min-h-screen flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md animate-fade-in">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mx-auto mb-4">
                        <img src="/images/logo-yodywhite.png" alt="YODY Logo" className="h-16" />
                    </div>
                    <h1 className="text-xl font-bold text-gray-800">
                        Vui lòng điền thông tin sản phẩm để kiểm tra hàng chính hãng.
                    </h1>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Số seri tem
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                name="productSerial"
                                value={formData.productSerial}
                                onChange={handleInputChange}
                                required
                                placeholder="Nhập số serial tem"
                                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:border-transparent ${serialValidation.isValid === true
                                    ? 'border-green-500 bg-green-50'
                                    : serialValidation.isValid === false
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-gray-300'
                                    }`}
                                style={{ '--tw-ring-color': '#277f4d', backgroundColor: serialValidation.isValid === null ? '#E0F7E0' : undefined }}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                {serialValidation.isChecking && (
                                    <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                                        style={{ borderColor: '#277f4d', borderTopColor: 'transparent' }}></div>
                                )}
                                {serialValidation.isValid === true && !serialValidation.isChecking && (
                                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                            d="M5 13l4 4L19 7"></path>
                                    </svg>
                                )}
                                {serialValidation.isValid === false && !serialValidation.isChecking && (
                                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                            d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                )}
                            </div>
                        </div>
                        {serialValidation.message && (
                            <p className={`mt-1 text-sm ${serialValidation.isValid === true ? 'text-green-600' : 'text-red-600'
                                }`}>{serialValidation.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Mã dưới lớp phủ cào
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                name="scratchCode"
                                value={formData.scratchCode}
                                onChange={handleInputChange}
                                required
                                placeholder="Nhập mã dưới lớp phủ cào"
                                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:border-transparent ${scratchCodeValidation.isValid === true
                                    ? 'border-green-500 bg-green-50'
                                    : scratchCodeValidation.isValid === false
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-gray-300'
                                    }`}
                                style={{ '--tw-ring-color': '#277f4d', backgroundColor: scratchCodeValidation.isValid === null ? '#E0F7E0' : undefined }}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                {scratchCodeValidation.isChecking && (
                                    <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                                        style={{ borderColor: '#277f4d', borderTopColor: 'transparent' }}></div>
                                )}
                                {scratchCodeValidation.isValid === true && !scratchCodeValidation.isChecking && (
                                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                            d="M5 13l4 4L19 7"></path>
                                    </svg>
                                )}
                                {scratchCodeValidation.isValid === false && !scratchCodeValidation.isChecking && (
                                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                            d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                )}
                            </div>
                        </div>
                        {scratchCodeValidation.message && (
                            <p className="mt-1 text-sm text-red-600">{scratchCodeValidation.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Số điện thoại người bán
                        </label>
                        <input
                            type="tel"
                            name="sellerPhone"
                            value={formData.sellerPhone}
                            onChange={handleInputChange}
                            required
                            placeholder="Nhập số điện thoại người bán"
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${phoneValidation.isValid === true
                                ? 'border-green-500 bg-green-50'
                                : phoneValidation.isValid === false
                                    ? 'border-red-500 bg-red-50'
                                    : 'border-gray-300'
                                }`}
                            style={{ '--tw-ring-color': '#277f4d', backgroundColor: phoneValidation.isValid === null ? '#E0F7E0' : undefined }}
                        />
                        {phoneValidation.message && (
                            <p className="mt-1 text-sm text-red-600">{phoneValidation.message}</p>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={loading || scratchCodeValidation.isChecking || serialValidation.isChecking}
                        className="w-full text-white py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        style={{ backgroundColor: '#277f4d' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#1f5f3a'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#277f4d'}
                    >
                        <span>
                            {loading
                                ? 'Đang xử lý...'
                                : (scratchCodeValidation.isChecking || serialValidation.isChecking)
                                    ? 'Đang kiểm tra...'
                                    : 'Kiểm tra'
                            }
                        </span>
                        {(loading || scratchCodeValidation.isChecking || serialValidation.isChecking) && (
                            <div
                                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        )}
                    </button>
                </form>

                {/* Error/Success Message */}
                {message.text && (
                    <div className="mt-4">
                        <div className={`p-4 rounded-lg ${message.type === 'error'
                            ? 'bg-red-50 border border-red-200 text-red-800'
                            : 'bg-green-50 border border-green-200 text-green-800'
                            }`}>
                            {message.text}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CheckCodePage;
